<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Pie } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

const props = defineProps({
  meta: {
    type: Object,
    required: true,
    default: () => ({
      total: 0,
      delivered: 0,
      read: 0,
      replied: 0,
    }),
  },
});

ChartJS.register(ArcElement, Tooltip, Legend);

const { t } = useI18n();

const hasData = computed(() => props.meta.total > 0);

const chartData = computed(() => {
  const { total, delivered, read, replied } = props.meta;
  // Calculate failed/pending/sent based on total - delivered (approx)
  // or rely on what's available.
  // Since we only have total, delivered, read, replied, the 'rest' are pending/failed/sent.
  // Ideally, valid states would be disjoint, but here 'read' implies 'delivered'.
  // Let's visualize the breakdown: Read vs Delivered (Unread) vs Others (Pending/Failed)

  const unreadDelivered = Math.max(0, delivered - read);
  const readNoReply = Math.max(0, read - replied);
  const others = Math.max(0, total - delivered);

  return {
    labels: [
      t('CAMPAIGN.DETAILS.METRICS.REPLIED'),
      t('CAMPAIGN.DETAILS.METRICS.READ'),
      t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.DELIVERED'),
      t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.PENDING'), // Grouping others as Pending/Failed
    ],
    datasets: [
      {
        backgroundColor: ['#10b981', '#3b82f6', '#5b5bd6', '#f59e0b'], // Green (Replied), Blue (Read), Purple (Delivered), Amber (Pending)
        data: [replied, readNoReply, unreadDelivered, others],
      },
    ],
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        usePointStyle: true,
        boxWidth: 8,
        color: '#64748b', // slate-500
        font: {
          size: 12,
        },
      },
    },
  },
};
</script>

<template>
  <div class="flex flex-col items-center justify-center p-4 h-full">
    <Pie v-if="hasData" :data="chartData" :options="chartOptions" />
    <div v-else class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.DETAILS.EMPTY') }}
    </div>
  </div>
</template>
